<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4">
    <div class="flex justify-between items-center mb-2">
      <h4 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Derivation Rule</h4>
      <button
        v-if="!isEditing"
        @click="$emit('start-editing')"
        class="text-xs px-2 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded hover:bg-primary-600/10 hover:text-primary-600 hover:border-primary-400 transition-colors"
      >
        Edit Rule
      </button>
    </div>

    <!-- View Mode -->
    <div v-if="!isEditing">
      <code class="block bg-gray-50 border border-gray-200 p-3 rounded text-sm font-mono text-gray-700">
        {{ item.rule }}
      </code>
      <div class="mt-3 flex flex-wrap gap-2">
        <span
          v-for="tag in (type === 'layer2' ? item.layer1_tags : item.layer2_flags) || []"
          :key="tag"
          @click="type === 'layer2' ? $emit('select-dimension', tag) : $emit('select-flag', tag)"
          class="px-2 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded text-sm cursor-pointer hover:bg-primary-600/10 hover:text-primary-600 hover:border-primary-400 transition-colors"
        >
          {{ formatTagName(tag) }}
        </span>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else>
      <textarea
        :value="ruleEditor.expression"
        @input="$emit('update-expression', $event.target.value)"
        class="w-full p-3 border border-gray-200 rounded font-mono text-sm bg-gray-50 text-gray-700 focus:bg-white focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
        rows="3"
        placeholder="SQL boolean expression..."
      ></textarea>
      <p class="text-xs text-gray-600 mt-1">
        {{ type === 'layer2' ? 'Reference Layer 0 columns: volume_intensity, panel_acuity, care_setting, etc.' : 'Reference Layer 1 flags: is_high_volume, is_geriatric, is_high_acuity, etc.' }}
      </p>

      <!-- Available flags (for Layer 3) -->
      <div v-if="type === 'layer3'" class="mt-2">
        <p class="text-xs text-gray-600 mb-1">Click to insert:</p>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="flag in patternsData?.layer2Flags"
            :key="flag.column"
            @click="insertFlag(flag.column)"
            class="px-2 py-0.5 bg-gray-50 border border-gray-200 text-gray-500 rounded text-xs hover:bg-primary-600/10 hover:text-primary-600 hover:border-primary-400 transition-colors"
          >
            {{ flag.column.toLowerCase() }}
          </button>
        </div>
      </div>

      <!-- Preview Results -->
      <div v-if="ruleEditor.preview" class="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
        <div class="flex gap-6 text-sm">
          <div>
            <span class="text-gray-500">Current:</span>
            <span class="font-semibold text-gray-700 ml-1">{{ formatNumber(ruleEditor.preview.current_count) }}</span>
          </div>
          <div>
            <span class="text-gray-500">After:</span>
            <span class="font-semibold text-gray-700 ml-1">{{ formatNumber(ruleEditor.preview.new_count) }}</span>
          </div>
          <div :class="ruleEditor.preview.diff > 0 ? 'text-green-600' : ruleEditor.preview.diff < 0 ? 'text-red-600' : 'text-gray-500'">
            <span>{{ ruleEditor.preview.diff > 0 ? '+' : '' }}{{ formatNumber(ruleEditor.preview.diff) }}</span>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="ruleEditor.error" class="mt-2 p-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded">
        {{ ruleEditor.error }}
      </div>

      <!-- Action Buttons -->
      <div class="mt-3 flex gap-2">
        <button
          @click="$emit('preview')"
          :disabled="ruleEditor.loading"
          class="px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-700 rounded text-sm hover:bg-gray-100 disabled:opacity-50 transition-colors"
        >
          {{ ruleEditor.loading ? 'Loading...' : 'Preview' }}
        </button>
        <button
          @click="$emit('save')"
          :disabled="ruleEditor.loading || !ruleEditor.preview"
          class="px-3 py-1.5 bg-primary-600 text-white rounded text-sm hover:bg-primary-600-hover disabled:opacity-50 transition-colors"
        >
          Save & Apply
        </button>
        <button
          @click="$emit('cancel-editing')"
          class="px-3 py-1.5 text-gray-500 text-sm hover:text-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RuleEditor',
  props: {
    item: {
      type: Object,
      required: true,
    },
    ruleEditor: {
      type: Object,
      required: true,
    },
    patternsData: {
      type: Object,
      default: null,
    },
    type: {
      type: String,
      required: true,
      validator: (val) => ['layer2', 'layer3'].includes(val),
    },
  },
  emits: ['start-editing', 'cancel-editing', 'preview', 'save', 'update-expression', 'select-dimension', 'select-flag'],
  computed: {
    isEditing() {
      return this.ruleEditor.editing && this.ruleEditor.tagId === this.item.tag_id
    },
  },
  methods: {
    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num?.toLocaleString() || '0'
    },
    formatTagName(name) {
      return name.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    insertFlag(column) {
      const flag = column.toLowerCase()
      const currentExpr = this.ruleEditor.expression || ''
      const newExpr = currentExpr ? `${currentExpr} AND ${flag}` : flag
      this.$emit('update-expression', newExpr)
    },
  },
}
</script>
